import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { addressDefs } from "my-hr/configs/my-hr-info-card-defs";
import {
  useMyHrChangeRequests,
  useMyHrChangeRequestsExaminersName,
  useMyHrUserRolesDataByContract,
} from "my-hr/hooks";
import { OcCustomTooltip, OcInfoCard, OcPageTransition } from "oc/components";
import { useOcCurrentUserState, useOcProperty } from "oc/hooks";

type MyHrContactsDataPanelProps = {
  employee: Record<string, any>; // Replace 'any' with a more specific type if available
  onEdit: (type: string, modifier: any) => void;
  hasChangeRequest: boolean;
  editable?: boolean;
};

export const MyHrContactsDataPanel = ({
  employee,
  onEdit,
  hasChangeRequest,
  editable = false,
}: MyHrContactsDataPanelProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { employeeCode, contractCode } = useOcCurrentUserState();

  const { data: changeRequests } = useMyHrChangeRequests(employeeCode);
  const { data: examinersData } = useMyHrChangeRequestsExaminersName({
    locale: i18n.language,
    contract: contractCode,
  });
  const { data: rolesData } = useMyHrUserRolesDataByContract({
    contract: contractCode,
  });

  const { data: changeSettingsEnabled } = useOcProperty({
    resource: "system",
    key: "changeRequestsEnabled",
    fetchPolicy: "network-only",
  });

  const isExaminer = rolesData?.some(
    (role) => role.product === "MyHR" && role.role === "examiner",
  );
  const hasExaminer = examinersData?.length > 0;
  const isDisabled = !hasExaminer && !isExaminer;
  const turnedOffChangeRequests = changeSettingsEnabled === "false";

  const isAddressRequestPending = (typeGroup: string) =>
    changeRequests?.some(
      (item: { typeGroup?: string; approvedAt?: any; rejectedAt?: any }) =>
        item?.typeGroup === typeGroup &&
        item?.approvedAt === null &&
        item?.rejectedAt === null,
    );

  const canAddNewAddress = [
    "permanentAddress",
    "temporaryAddress",
    "serviceAddress",
  ].some((key) => {
    const city = employee?.[key]?.city;
    return !city?.length && !isAddressRequestPending(key);
  });

  return (
    <OcPageTransition>
      <Box id="tab-panel-container">
        {addressDefs.map((def, index) => (
          <Box key={`${def.type}-${index}`} paddingBottom="16px">
            <OcInfoCard
              def={def}
              data={employee}
              onEdit={onEdit}
              hasChangeRequest={hasChangeRequest}
              editable={!isDisabled && editable}
            />
          </Box>
        ))}
        {!turnedOffChangeRequests &&
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            {!isDisabled ? (
              <Button
                id="add-new-address-button"
                onClick={() => onEdit("newAddress", null)}
                variant="text"
                disabled={!canAddNewAddress}
                style={{
                  backgroundColor: theme.palette.gray30.main,
                  color: theme.palette.darkBase.main,
                }}
              >
                {t("MY_HR_ADD_NEW_ADDRESS")}
              </Button>
            ) : (
              <OcCustomTooltip
                title={t("OC_NOT_SET_EXAMINER_INFO")}
                placement="top"
              >
                <Button
                  id="add-new-address-button"
                  style={{
                    backgroundColor: theme.palette.gray30.main,
                    color: theme.palette.gray40.main,
                  }}
                >
                  {t("MY_HR_ADD_NEW_ADDRESS")}
                </Button>
              </OcCustomTooltip>
            )}
          </Box>
        }
        <Box height="25px" />
      </Box>
    </OcPageTransition>
  );
};

export default MyHrContactsDataPanel;
